import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers/display-toast';
import { confirmPasswordReset, signIn } from '@/lib/auth';
import { CreatePassword } from '@/modules/authorization/create-password/components/create-password';
import { OpsiumUser } from '@/modules/common/types/opsium-user';
import { useRootSelector } from '@/router/state/root/util/context/helpers';

interface ConfirmResetPasswordFormProps {
  email: string;
  code: string;
  onSuccess: (user: OpsiumUser) => void;
}

export const ConfirmResetPasswordForm: React.FC<ConfirmResetPasswordFormProps> = ({
  email,
  code,
  onSuccess,
}) => {
  const { t } = useTranslation('unauthenticated.resetPassword');
  const isRootStateLoading = useRootSelector(state => state.context.isLoading);
  const mutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      await confirmPasswordReset(email, code, password);
      return await signIn(email, password);
    },
    onSuccess: user => {
      displayToastSuccess(t('passwordResetSuccess'));
      onSuccess(user);
    },
    onError: (error: any) => {
      displayToastError(error.message);
    },
  });

  return (
    <CreatePassword
      email={email}
      header={t('header')}
      subtitle={t('confirmSubtitle')}
      passwordLabel={t('newPasswordLabel')}
      passwordPlaceholder={t('newPasswordPlaceholder')}
      ctaLabel={t('resetPasswordCta')}
      isLoading={mutation.isPending || isRootStateLoading}
      onSubmit={async ({ email, password }) => {
        await mutation.mutateAsync({ email, password });
      }}
    />
  );
};
